import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { changeNumComma } from "../../../../AdminApi/commFunc";
import { useApi } from "../../../../hooks/useApi";
import RadioBtn from "../../../Button/RadioBtn";
import Loader from "../../../DefaultStyles/Loader";
import { Input } from "../../../Input/Input";
import { Textarea } from "../../../Input/Textarea";
import AlertModal from "../../../Modal/AlertModal";
import DefaultModalLayout from "../../../Modal/DefaultModalLayout";
import { ModalSectionLabel } from "../../Party/PartyDetail/PartyExitModal/PartyExitModal";
import { ModalRow } from "./BlackListModal/BlackListModal";
import TargetContainer, {
  CommentTargetContainer,
  ModalLabel,
} from "./CommentModal/TargetContainer";
import CheckBox from "../../../Button/CheckBox";

interface UserImmediatePayModalProps {
  closeEvent: () => void;
  confirmEvent: () => void;
  userData: {
    uid: string;
    name: string;
    nickname: string;
  };
}

export default function UserImmediatePayModal({
  closeEvent,
  confirmEvent,
  userData,
}: UserImmediatePayModalProps) {
  const [checkAlert, setCheckAlert] = useState(false);
  const [targetUid, setTargetUid] = useState(
    userData ? `${userData?.uid}_${userData?.name}_${userData?.nickname}` : ""
  );
  const [money, setMoney] = useState("");
  const [reason, setReason] = useState("");
  const [check, setCheck] = useState({
    first: false,
    second: false,
    third: false,
  });
  const [moneyCharge, setMoneyCharge] = useState(false);
  const [pay, setPay] = useState(false);
  const [error, setError] = useState({
    message: "" as string | undefined,
    modal: false,
  });

  const WITHDRAW_ACCOUNTS = [
    { label: "mk(기업은행)", account: "01031289886", bankCode: "003" },
    { label: "jason(우리은행)", account: "1002539945032", bankCode: "020" },
    { label: "volt(우리은행)", account: "1002138743794", bankCode: "020" },
  ] as const;

  const [selectedAccount, setSelectedAccount] = useState<{
    account: string;
    bankCode: string;
  }>({ account: "", bankCode: "" });
  const isUidOne = userData?.uid === "1";

  const {
    response,
    loading,
    error: billError,
  } = useApi({
    method: "post",
    url: pay
      ? check?.third
        ? `pay/withdraw`
        : check?.first || moneyCharge
        ? `user/bill`
        : check?.second
        ? `user/substractMoney`
        : undefined
      : undefined,
    data: check?.third
      ? {
          uid: 1,
          amount: Number(money?.replaceAll(",", "")),
          accountnum: selectedAccount.account,
          bankcode: selectedAccount.bankCode,
          reason,
        }
      : check?.first || moneyCharge
      ? {
          uid: Number(targetUid?.split("_")?.[0]) ?? 0,
          amount: Number(money?.replaceAll(",", "")),
          money: moneyCharge ? 1 : 0,
          reason,
        }
      : {
          uid: Number(targetUid?.split("_")?.[0]) ?? 0,
          amount: Number(money?.replaceAll(",", "")),
          reason,
        },
  });

  useEffect(() => {
    if (response?.status === 200 || response?.status === 201) {
      closeEvent();
      confirmEvent();

      toast.success(
        check?.third ? "인출이 완료되었습니다." : "즉시 결제가 완료되었습니다."
      );
    }
  }, [closeEvent, confirmEvent, response, check?.third]);

  useEffect(() => {
    if (billError) {
      setPay(false);
      setError({
        modal: true,
        message: billError?.data ?? "결제 실패",
      });
    }
  }, [billError]);

  useEffect(() => {
    if (check.first) {
      setMoneyCharge(true);
    } else {
      setMoneyCharge(false);
    }
  }, [check]);

  return (
    <DefaultModalLayout
      closeEvent={() => closeEvent()}
      confirmEvent={() => setCheckAlert(true)}
      modalTitle="링키드 머니 관리"
      confirmDisabled={
        (Object.values(check).filter((item) => item === true).length === 0 &&
          !moneyCharge) ||
        !targetUid?.includes("_") ||
        money === "" ||
        reason === "" ||
        (check?.third && selectedAccount.account === "")
      }
    >
      {/* 대상 */}
      <ModalRow>
        <TargetContainer targetUid={targetUid} setTargetUid={setTargetUid} />
      </ModalRow>

      {/* 금액 */}
      <ModalRow>
        <ModalLabel>금액</ModalLabel>

        <CommentTargetContainer>
          <Input
            placeholder="금액 입력"
            onChange={(e) => {
              setMoney(changeNumComma(e).toLocaleString("ko"));
            }}
            value={money}
            inputMode="numeric"
            type="text"
          />
        </CommentTargetContainer>
      </ModalRow>

      {/* 구분 */}
      <ModalRow
        style={{
          alignItems: "flex-start",
          flexDirection: "column",
          marginBottom: 11,
        }}
      >
        <ModalLabel>구분</ModalLabel>

        <CommentTargetContainer
          style={{ marginTop: 12, gap: 42, alignItems: "flex-start" }}
        >
          <RadioBtn
            label="카드 결제"
            clickEvent={() =>
              setCheck({
                first: !check.first,
                second: false,
                third: false,
              })
            }
            check={check.first}
          />

          <RadioBtn
            label="링키드 머니 차감"
            clickEvent={() =>
              setCheck({
                second: !check.second,
                first: false,
                third: false,
              })
            }
            check={check.second}
            noTooltip={true}
          />

          {isUidOne ? (
            <RadioBtn
              label="인출하기"
              clickEvent={() => {
                setCheck({
                  first: false,
                  second: false,
                  third: !check.third,
                });
                setMoneyCharge(false);
                if (!check.third)
                  setSelectedAccount({ account: "", bankCode: "" });
              }}
              check={check.third}
              noTooltip={true}
            />
          ) : <></>}
        </CommentTargetContainer>
      </ModalRow>

      <div style={{ marginLeft: 28, marginBottom: 24 }}>
        <CheckBox
          checked={moneyCharge}
          onChange={(e: {
            target: {
              checked: boolean | ((prevState: boolean) => boolean);
            };
          }) => {
            setMoneyCharge(e.target.checked);
          }}
          label="링키드 머니 충전"
          disabled={check.second || check.third}
          labelColor="#858c97"
        />
      </div>

      {/* 인출 계좌 선택 */}
      {check.third ? (
        <ModalRow
          style={{
            alignItems: "flex-start",
            flexDirection: "column",
            marginBottom: 11,
          }}
        >
          <ModalLabel>인출 계좌</ModalLabel>

          <CommentTargetContainer
            style={{
              marginTop: 12,
              gap: 12,
              alignItems: "flex-start",
              flexDirection: "column",
            }}
          >
            {WITHDRAW_ACCOUNTS.map((item) => (
              <RadioBtn
                key={item.label}
                label={`${item.label}: ${item.account}`}
                clickEvent={() =>
                  setSelectedAccount({
                    account: item.account,
                    bankCode: item.bankCode,
                  })
                }
                check={selectedAccount.account === item.account}
                noTooltip={true}
              />
            ))}
          </CommentTargetContainer>
        </ModalRow>
      ) : <></>}

      <ModalSectionLabel>사유</ModalSectionLabel>

      <Textarea
        placeholder="사유 입력 (필수)"
        style={{ marginTop: 16 }}
        onChange={(e) => setReason(e.target.value)}
        value={reason}
      />

      <>
        {checkAlert && (
          <AlertModal
            cancelEvent={() => setCheckAlert(false)}
            title={`${targetUid}님의 \n${
              check?.third
                ? `인출하기 (${selectedAccount.account})`
                : moneyCharge
                ? "링키드 머니 충전"
                : check?.first
                ? "카드 결제"
                : "링키드 머니 차감"
            }_${money}원이 ${check?.third ? "인출" : "결제"}됩니다. 계속하시겠습니까?`}
            confirmText="계속"
            confirmEvent={() => {
              setCheckAlert(false);
              setPay(true);
            }}
          />
        )}

        {/* 에러 모달 */}
        {error?.modal && (
          <AlertModal
            title={error?.message}
            customCancelBtn={<></>}
            confirmEvent={() =>
              setError({
                ...error,
                modal: false,
              })
            }
          />
        )}

        {loading && <Loader />}
      </>
    </DefaultModalLayout>
  );
}
